import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { theme } from './theme';
import { FormErrorProvider } from 'react-hook-form-mui';
import { router } from './routes';
import { QueryClientProvider } from '@contexts/queryClient';
import { SnackbarProvider } from '@contexts/snackbar';
import { RouterProvider } from 'react-router';
import { AuthProvider } from './contexts/auth';
import { AxiosInterceptorProvider } from './contexts/axios';
import { useTranslation } from 'react-i18next';
import { PageTitleProvider } from './contexts/pagetitle';
import { z } from 'zod';
import i18next from 'i18next';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { getStoredLanguage } from './services/storage';
import { NotificationProvider } from './contexts/notificationsCount';
import { MenuProvider } from './contexts/menu';

z.setErrorMap((issue, ctx) => {
  const messageFromI18n = i18next.t(`translation:${issue.message}`);
  return { message: messageFromI18n || ctx.defaultError };
});

// Configure Day.js plugins
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(utc);

const getLanguage = (): string => {
  const lng: string | null = getStoredLanguage();
  if (lng != null) {
    if (lng.startsWith('pt')) return 'pt-br';
    else if (lng.startsWith('es')) return 'es';
  }

  return 'en';
};

function App() {
  const { t } = useTranslation();

  // Set the dayjs locale globally
  const locale = getLanguage();
  dayjs.locale(locale);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <ThemeProvider theme={theme}>
        <GlobalStyles
          styles={{
            '.MuiPickersOutlinedInput-root': {
              borderRadius: '12px !important',
              backgroundColor: '#FFFFFF',
              color: '#2D354A',
              borderColor: '#B3BCCC',
            },
          }}
        />
        <SnackbarProvider>
          <CssBaseline />
          <MenuProvider>
            <NotificationProvider>
              <FormErrorProvider
                onError={(error) => {
                  if (error?.type === 'required')
                    return t('This field is required');
                  return error?.message || t('Unknown error');
                }}
              >
                <QueryClientProvider>
                  <AuthProvider>
                    <AxiosInterceptorProvider>
                      <PageTitleProvider>
                        <RouterProvider router={router} />
                      </PageTitleProvider>
                    </AxiosInterceptorProvider>
                  </AuthProvider>
                </QueryClientProvider>
              </FormErrorProvider>
            </NotificationProvider>
          </MenuProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
